import { useProfileLinkProps } from "@circle-react/hooks/profile/useProfileLinkProps";
import type { CommunityMember } from "@circle-react/types";

import { t } from "@/i18n-js/instance";
import { ConnectionNote } from "@/react/components/ProfileModalV3/Body/ConnectBanner/ConnectionNote";
import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";
import { useCircleV4 } from "@/react/hooks/useCircleV4";
import { useSmScreenMediaQuery } from "@/react/hooks/useMediaQuery";

import { ConnectionButtonsOrchestrator } from "../../ConnectionButtons/ConnectionButtonsOrchestrator";
import { useConnectionButtonState } from "../../ConnectionButtons/hooks/useConnectionButtonState";
import { Icon } from "../../shared/Icon";
import { MemberCardImage } from "../../shared/MembersList/CommunityMembers/CardView/MemberCardImage";
import { MobileCompassMemberRow } from "../../shared/MembersList/CommunityMembers/ListView/CompassMemberRow/MobileCompassMemberRow";
import { useIsMemberDirectoryMapAvailable } from "../useIsMemberDirectoryMapAvailable";
import { DismissRecommendation } from "./DismissRecommendation";
import type { ExpandableAccordionItem } from "./ExpandableAccordion";

export interface ExpandableListItemProps {
  communityMember: CommunityMember;
  item: ExpandableAccordionItem;
}

export const ExpandableListItem = ({
  communityMember,
  item,
}: ExpandableListItemProps) => {
  const recommendationId =
    item.type === "recommendation" ? Number.parseInt(item.id, 10) : undefined;

  const profileLinkProps = useProfileLinkProps({
    public_uid: communityMember?.public_uid ?? "",
    params: recommendationId
      ? { recommendation_id: recommendationId }
      : undefined,
  });

  const isMobile = useSmScreenMediaQuery();
  const { isCircleV4 } = useCircleV4();
  const { isAvailable: isLocationVisible } = useIsMemberDirectoryMapAvailable();

  const {
    isPendingReceived: isOtherUserSentConnection,
    connection: memberConnection,
  } = useConnectionButtonState(communityMember);

  const mutualConnectionsCount = communityMember.mutual_connections_count ?? 0;

  if (!communityMember) {
    return null;
  }

  if (isMobile && isCircleV4) {
    return (
      <li
        className="border-primary bg-primary group rounded-xl px-6 py-4"
        data-testid="expandable-list-item"
      >
        <MobileCompassMemberRow
          member={communityMember}
          activeStateDisplay="badge"
          showMomentumModal={false}
          iconOnly
          recommendationId={recommendationId}
          profileLinkParams={
            recommendationId
              ? { recommendation_id: recommendationId }
              : undefined
          }
          topRightExtra={
            item.type === "recommendation" ? (
              <DismissRecommendation recommendationId={item.id} />
            ) : null
          }
        />
      </li>
    );
  }

  return (
    <li
      className="border-primary bg-primary group flex w-full min-w-0 flex-1 gap-x-4 rounded-xl px-6 py-4"
      data-testid="expandable-list-item"
    >
      <div style={{ aspectRatio: "1 / 1" }} className="h-10">
        <a {...profileLinkProps}>
          <MemberCardImage
            name={communityMember.name ?? ""}
            src={communityMember.avatar_url ?? ""}
            className="!rounded-full"
          />
        </a>
      </div>
      <div className="flex w-full min-w-0 flex-1 flex-col gap-y-3">
        <div className="flex w-full min-w-0 flex-1 flex-col md:flex-row justify-between gap-y-3">
          <a {...profileLinkProps} className="min-w-0 flex-1">
            <div className="flex min-w-0 flex-col gap-y-3">
              <div className="flex flex-col">
                <Typography.LabelSm
                  truncate
                  color="text-dark"
                  weight="semibold"
                >
                  {communityMember.name}
                </Typography.LabelSm>

                <Typography.BodyXs truncate color="text-default">
                  {communityMember.headline}
                </Typography.BodyXs>
              </div>

              {!!mutualConnectionsCount ||
                (communityMember.location && isLocationVisible && (
                  <div className="flex gap-4">
                    {communityMember.location && isLocationVisible && (
                      <div className="hidden items-center gap-1 md:flex">
                        <Typography.LabelXs truncate color="text-default">
                          <Icon type="16-location" size={16} />
                          {communityMember.location}
                        </Typography.LabelXs>
                      </div>
                    )}
                    {!!mutualConnectionsCount && (
                      <div className="hidden items-center gap-1 md:flex">
                        <Typography.LabelXs truncate color="text-default">
                          <Icon type="16-members" size={16} />
                          {t("members_directory.mutual_connections_count", {
                            count: mutualConnectionsCount,
                          })}
                        </Typography.LabelXs>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </a>

          {isOtherUserSentConnection && memberConnection?.note && (
            <div className="md:ml-0 block md:hidden">
              <ConnectionNote
                note={memberConnection?.note}
                connectionId={memberConnection?.id?.toString()}
                initiatorIsAdmin={memberConnection?.initiator?.admin}
                member={communityMember}
              />
            </div>
          )}

          <div className="flex w-fit items-start gap-2">
            <ConnectionButtonsOrchestrator
              member={communityMember}
              acceptButtonPosition={isMobile ? "left" : "right"}
              activeStateDisplay="badge"
              showMomentumModal={false}
              recommendationId={recommendationId}
            />
            {item.type == "recommendation" && (
              <DismissRecommendation recommendationId={item.id} />
            )}
          </div>
        </div>

        {isOtherUserSentConnection && memberConnection?.note && (
          <div className="ml-[-56px] md:ml-0 hidden md:block">
            <ConnectionNote
              note={memberConnection?.note}
              connectionId={memberConnection?.id?.toString()}
              initiatorIsAdmin={memberConnection?.initiator?.admin}
              member={communityMember}
            />
          </div>
        )}
      </div>
    </li>
  );
};
